import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { SVG_MUI } from "@ryde/assets";
import {
  IStyledAttributes,
  NO_VALUE_DISPLAY,
  OccupancyEnum,
  RideStatusEnum,
} from "@ryde/shared";

import { FramerTooltip } from "../tooltips";
import { getColorByOccupancy } from "./ride.controls.occupancy.utils";

export interface IVehicleOccupancyControlBase {
  $busOccupancy: OccupancyEnum;
}

const OccupancyColoredLabelStyled = styled.label.attrs<IStyledAttributes>({
  "data-e2e": "occupancy-color-label",
})<IVehicleOccupancyControlBase>`
  color: ${({ $busOccupancy }) => getColorByOccupancy($busOccupancy)};
`;

interface IVehicleOccupancyColoredLabel extends IVehicleOccupancyControlBase {
  vehicleCapacity: number | typeof NO_VALUE_DISPLAY | undefined;
  seatsCount: number | undefined;
}

interface IVehicleOccupancyIconAndColoredLabel extends IVehicleOccupancyColoredLabel {
  rideStatus: RideStatusEnum;
}

export const VehicleOccupancyColoredLabel: FC<
  IVehicleOccupancyColoredLabel
> = ({ vehicleCapacity, seatsCount, $busOccupancy }) => {
  const busCapacity = vehicleCapacity || 0;
  const seatsCountNormalized = seatsCount || 0;
  const { t } = useTranslation();
  return (
    <FramerTooltip
      content={t("ride.RIDE_PASSENGERS_OUT_OF_TOTAL", {
        numPassengers: seatsCountNormalized,
        numSeats: busCapacity,
      })}
    >
      <OccupancyColoredLabelStyled $busOccupancy={$busOccupancy}>
        {seatsCountNormalized}/{busCapacity}
      </OccupancyColoredLabelStyled>
    </FramerTooltip>
  );
};

export const VehicleOccupancyBusIcon: FC<IVehicleOccupancyControlBase> = ({
  $busOccupancy,
}) => {
  return (
    <>
      {$busOccupancy === OccupancyEnum.High && <SVG_MUI.BusGreenSVG />}
      {$busOccupancy === OccupancyEnum.Medium && <SVG_MUI.BusYellowSVG />}
      {$busOccupancy === OccupancyEnum.Low && <SVG_MUI.BusRedSVG />}
    </>
  );
};

export const VehicleOccupancyIconAndColoredLabel: FC<
  IVehicleOccupancyIconAndColoredLabel
> = ({ $busOccupancy, seatsCount, vehicleCapacity, rideStatus }) => {
  const isDraftStatus = rideStatus === RideStatusEnum.Draft;
  const busOccupancyComputed = isDraftStatus
    ? OccupancyEnum.Low
    : $busOccupancy;
  const vehicleCapacityComputed = isDraftStatus
    ? NO_VALUE_DISPLAY
    : vehicleCapacity;

  return (
    <>
      <VehicleOccupancyBusIcon $busOccupancy={busOccupancyComputed} />
      <VehicleOccupancyColoredLabel
        $busOccupancy={busOccupancyComputed}
        vehicleCapacity={vehicleCapacityComputed}
        seatsCount={seatsCount}
      />
    </>
  );
};
